<script setup lang="ts">
import { PLAN_ENUM } from '@/components/organisation/interface/IInstituteSubscriptionInfo'
import { LANGUAGEMETA } from '@/utils/meta'
import { languagesItems, type ILanguage } from '@/utils/sharedData/languages'

import { onMounted, ref, watch, computed } from 'vue'
import ideService from '@/services/ide/ide.service'
import settingService from '@/services/ide/settings/setting.service'
import fontsizeService from '@/services/ide/fontsize.service'
import { useAuthStore } from '@/stores/auth.store'
import { useIdeStore } from '@/stores/ide.store'
import { useOrganisationStore } from '@/stores/organisation.store'
import { useJdroidStore } from '@/stores/jdroid.store'
import { useSubscribeStore } from '@/stores/subscribe.store'

import ideModal from '@/components/ide/ide/IdeModal.vue'
import Button from '@/components/atoms/button/index.vue'

import { cloneDeep } from 'lodash-es'
import { cn } from '@/utils/cn'
import { IDEVIEWMODELS } from '@/utils/models'

interface IThemes {
  name: string
  value: string
}

interface ISettings {
  fontSize: number
  theme: string
  defaultLanguage: string | null
  defaultVersionIndex: number | null
  defaultExecutionMode: boolean
  autoCompleteCode: boolean
}

// Light themes
const lightThemes: IThemes[] = [
  { name: 'X Code', value: 'xcode' },
  { name: 'Chrome', value: 'chrome' },
  { name: 'Clouds', value: 'clouds' },
  { name: 'Crimson Editor', value: 'crimson-editor' },
  { name: 'Dawn', value: 'dawn' },
  { name: 'Dreamweaver', value: 'dreamweaver' },
  { name: 'Eclipse', value: 'eclipse' },
  { name: 'GitHub Light Default', value: 'github-light-default' },
  { name: 'GitHub (Legacy)', value: 'github' },
  { name: 'IPlastic', value: 'iplastic' },
  { name: 'Solarized Light', value: 'solarized-light' },
  { name: 'TextMate', value: 'textmate' },
  { name: 'Tomorrow', value: 'tomorrow' },
  { name: 'Kuroir', value: 'kuroir' },
  { name: 'KatzenMilch', value: 'katzenmilch' },
  { name: 'SQL Server', value: 'sqlserver' },
  { name: 'CloudEditor', value: 'cloud-editor' }
]

// Dark themes
const darkThemes: IThemes[] = [
  { name: 'Chaos', value: 'chaos' },
  { name: 'Ambiance', value: 'ambiance' },
  { name: 'Clouds Midnight', value: 'clouds-midnight' },
  { name: 'Dracula', value: 'dracula' },
  { name: 'Cobalt', value: 'cobalt' },
  { name: 'Gruvbox', value: 'gruvbox' },
  { name: 'Green on Black', value: 'gob' },
  { name: 'idle Fingers', value: 'idle-fingers' },
  { name: 'krTheme', value: 'kr-theme' },
  { name: 'Merbivore', value: 'merbivore' },
  { name: 'Merbivore Soft', value: 'merbivore-soft' },
  { name: 'Mono Industrial', value: 'mono-industrial' },
  { name: 'Monokai', value: 'monokai' },
  { name: 'Nord Dark', value: 'nord-dark' },
  { name: 'One Dark', value: 'one-dark' },
  { name: 'Pastel on dark', value: 'pastel-on-dark' },
  { name: 'Solarized Dark', value: 'solarized-dark' },
  { name: 'Terminal', value: 'terminal' },
  { name: 'Tomorrow Night', value: 'tomorrow-night' },
  { name: 'Tomorrow Night Blue', value: 'tomorrow-night-blue' },
  { name: 'Tomorrow Night Bright', value: 'tomorrow-night-bright' },
  { name: 'Tomorrow Night 80s', value: 'tomorrow-night-eighties' },
  { name: 'Twilight', value: 'twilight' },
  { name: 'Vibrant Ink', value: 'vibrant-ink' },
  { name: 'GitHub Dark', value: 'github-dark' },
  { name: 'CloudEditor Dark', value: 'cloud-editor-dark' }
]

const authStore = useAuthStore()
const ideStore = useIdeStore()
const organisationStore = useOrganisationStore()
const jdroidStore = useJdroidStore()
const subscribeStore = useSubscribeStore()

const languages: ILanguage[] = cloneDeep(languagesItems)
  .filter((lang: ILanguage) => !lang.isAdvanced)
  .sort((a: ILanguage, b: ILanguage) => (a.displayName > b.displayName ? 1 : -1))
const fontSizeSelect = ref<HTMLSelectElement | null>(null)

const isLoading = ref<boolean>(false)
const isCompleted = ref<boolean>(false)
const errorMsg = ref<string>('')
const settingsForm = ref<ISettings>({
  fontSize: 12,
  theme: lightThemes[0].value,
  defaultLanguage: null,
  defaultVersionIndex: 0,
  defaultExecutionMode: true,
  autoCompleteCode: false
})

const isUserloggedIn = computed(() => {
  return authStore.isInitiated && authStore.isUserloggedIn
})
const isFetchingSubscriptionInfo = computed(() => {
  return subscribeStore.isFetchingSubscriptionInfo
})

const isFreePlan = computed(() => {
  return accountType.value === PLAN_ENUM.FREE
})

const isCustom = computed(() => {
  return subscribeStore.bundleInitData?.isCustom
})

const showGetMoreCredits = computed(() => {
  return (
    isUserloggedIn.value && !isFetchingSubscriptionInfo.value && isFreePlan.value && !isCustom.value
  )
})

const isInitAndLoggedIn = computed(() => {
  return authStore.isInitiated && authStore.isUserloggedIn
})

const accountType = computed(() => {
  return (organisationStore.instituteSubscriptionInfo?.plan as string) || null
})
const isPremiumComputed = computed(() => {
  if (!authStore.isUserloggedIn) return false
  if (organisationStore.isIndividualAccount) {
    return authStore.isPremium
  } else if (organisationStore.isOrganisationOwner || organisationStore.isOrganisationAdmin) {
    if (accountType.value != PLAN_ENUM.FREE) {
      return true
    } else {
      return false
    }
  } else {
    if (organisationStore.isPremiumIde) {
      return true
    } else {
      return false
    }
  }
})
const isPremium = ref<boolean>(isPremiumComputed.value)
const fontSizeEvent = ref<Event | null>(null)
const isDark = ref<boolean>(ideStore.colorMode == 'dark')
const versions = ref<string[]>([])
const lightTheme = ref<string | null>(null)
const darkTheme = ref<string | null>(null)
const settings = computed(() => {
  return ideStore.ideSettings
})

const themesMode = computed(() => {
  if (!isDark.value) {
    return lightThemes
  } else {
    return darkThemes
  }
})

/**
 * Checks if the version is selected
 * @param index The version index to check
 * @returns True if the version is selected
 */
const isVersionSelected = (index: number) => {
  return settingsForm.value.defaultVersionIndex === index
}
/**
 * @description change theme and update embed
 */
const selectTheme = async () => {
  if (ideStore.codeEditor) {
    ideStore.codeEditor.setTheme(`ace/theme/${settingsForm.value.theme}`)
    if (ideStore.ideSettings !== null) {
      ideStore.ideSettings.theme = settingsForm.value.theme
    }
  }
}
/**
 * Checks if the font size is selected
 * @param fontSize The font size to check
 * @returns True if the font size is selected
 */
const isFontsizeSelected = (fontSize: number) => {
  return ideStore.fontSize === fontSize
}

/**
 * @param event onchange event
 */
const saveEvent = (event: Event) => {
  fontSizeEvent.value = event
}

/**
 * Sets the font size
 */
const setFontSize = () => {
  if (fontSizeEvent.value) {
    fontsizeService.changeIdeFontSize(fontSizeEvent.value)
    settingsForm.value.fontSize = ideStore.fontSize
  }
}

/**
 * Sets the initial font size from the settings
 */
const setInitialFontSize = () => {
  if (fontSizeSelect.value) {
    fontSizeSelect.value!.value = settingsForm.value.fontSize.toString()
    fontSizeSelect.value!.dispatchEvent(new Event('change'))
  }
}

/**
 * Resets http error msgs
 */
const handleResetHttpMsg = () => {
  isLoading.value = false
  isCompleted.value = true
  setTimeout(() => {
    isCompleted.value = false
    errorMsg.value = ''
  }, 3000)
}

/**
 * wait for ace and code editor to be initialized
 * @param count The count
 * @returns The promise
 */
const postAceAndCodeEditorInit = async (count: number = 0): Promise<boolean> => {
  if (count > 120) {
    return false
  } else {
    await new Promise((resolve) => setTimeout(resolve, 100))
    if (
      authStore.isInitiated &&
      authStore.isUserloggedIn &&
      useIdeStore().codeEditor &&
      useIdeStore().isIdeSettingsCompleted
    ) {
      return true
    } else {
      return postAceAndCodeEditorInit(count + 1)
    }
  }
}

/**
 * Init IDE settings
 */
const initIdeSetting = async () => {
  await postAceAndCodeEditorInit().then(async (res) => {
    if (res) {
      if (settings.value) {
        settingsForm.value.fontSize = settings.value?.fontSize || 12
        const theme = settings.value.savedTheme
        if (ideStore.colorMode === 'dark') {
          const isDarkTheme = darkThemes.some(({ value }) => value === theme)
          settingsForm.value.theme = isDarkTheme ? theme : darkThemes[0].value
          darkTheme.value = isDarkTheme ? theme : darkThemes[0].value
          lightTheme.value = !isDarkTheme ? theme : lightThemes[0].value
        } else {
          const isLightTheme = lightThemes.some(({ value }) => value === theme)
          settingsForm.value.theme = isLightTheme ? theme : lightThemes[0].value
          darkTheme.value = !isLightTheme ? theme : darkThemes[0].value
          lightTheme.value = isLightTheme ? theme : lightThemes[0].value
        }
        settingsForm.value.defaultLanguage = settings.value?.defaultLanguage || null
        settingsForm.value.defaultVersionIndex = settings.value?.defaultVersionIndex || null
        settingsForm.value.autoCompleteCode = settings.value?.autoCompleteCode || false
        settingsForm.value.defaultExecutionMode = settings.value?.defaultExecutionMode || false

        ideStore.setInteractiveMode(settingsForm.value.defaultExecutionMode)
        ideStore.codeEditor.setOptions({
          enableLiveAutocompletion: settingsForm.value.autoCompleteCode
        })
        setInitialFontSize()
        selectTheme()
      } else {
        settingsForm.value.defaultLanguage = null
        settingsForm.value.defaultVersionIndex = null
        if (ideStore.colorMode === 'dark') {
          settingsForm.value.theme = darkThemes[0].value
          darkTheme.value = darkThemes[0].value
          lightTheme.value = lightThemes[0].value
        } else {
          settingsForm.value.theme = lightThemes[0].value
          darkTheme.value = darkThemes[0].value
          lightTheme.value = lightThemes[0].value
        }
      }
    }
  })
}

/**
 * Set Dark Mode state
 * @param dark defines change mode to dark or not
 */
const setDarkMode = (dark: boolean) => {
  isDark.value = dark
  localStorage.setItem('jdoodle-mode', dark ? 'dark' : 'light')
  ideStore.setColorMode(localStorage.getItem('jdoodle-mode'))

  if (!dark) {
    darkTheme.value = settingsForm.value.theme
    if (ideStore.ideSettings) ideStore.ideSettings.theme = lightTheme.value || lightThemes[0].value
    settingsForm.value.theme = lightTheme.value || lightThemes[0].value
  } else {
    lightTheme.value = settingsForm.value.theme
    if (ideStore.ideSettings) ideStore.ideSettings.theme = darkTheme.value || darkThemes[0].value
    settingsForm.value.theme = darkTheme.value || darkThemes[0].value
  }
}
/**
 * Wait for the IDE to be initiated
 * @param count The count
 * @returns boolean
 */
const postNotInit = async (count: number = 0): Promise<boolean> => {
  if (count > 1200) {
    return false
  } else {
    if (authStore.isInitiated) {
      return authStore.isUserloggedIn
    } else {
      await new Promise((resolve) => setTimeout(resolve, 100))
      return await postNotInit(count + 1)
    }
  }
}

/**
 * Set the theme for not logged in users
 */
const setNotLoggedInTheme = async () => {
  const isLoggedIn = await postNotInit()
  const localStorageHasTheme = localStorage.getItem('jdoodle-mode')
  if (!isLoggedIn || localStorageHasTheme === null) {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      ideStore.setColorMode('dark')
    } else {
      ideStore.setColorMode('light')
    }
  }
}

/**
 * Submits the IDE settings to be updated
 */
const onSubmit = async () => {
  setFontSize()
  if (!isPremium.value) {
    handleResetHttpMsg()
  } else {
    isLoading.value = true
    await ideService
      .updateIdeSetting(settingsForm.value)
      .then(() => {
        selectTheme()
        ideStore.setInteractiveMode(settingsForm.value.defaultExecutionMode)
        ideStore.codeEditor.setOptions({
          enableLiveAutocompletion: settingsForm.value.autoCompleteCode
        })
      })
      .catch((err) => {
        errorMsg.value = err.data.message
      })
      .finally(() => {
        handleResetHttpMsg()
      })
  }
}

/**
 * Upgrade pro plan handler
 */
const handleUpgradePlan = () => {
  settingService.closePopup(IDEVIEWMODELS.IDESETTINGS)
}

onMounted(async () => {
  // Init theme
  await setNotLoggedInTheme()
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    setNotLoggedInTheme()
  })
  await initIdeSetting()
})

// Watch the change in other tabs/windows
window.addEventListener('storage', function (e) {
  if (e.key === 'jdoodle-mode') {
    ideStore.setColorMode(e.newValue)
    setDarkMode(e.newValue === 'dark')
  }
})

watch(
  () => isInitAndLoggedIn.value,
  (val) => {
    if (val) {
      ideStore.setColorMode(localStorage.getItem('jdoodle-mode'))
    }
  }
)

watch(
  () => settingsForm.value.defaultLanguage,
  (val) => {
    if (val && LANGUAGEMETA[val]) {
      versions.value = LANGUAGEMETA[val].versions || []
      if (settings.value?.defaultLanguage === val && settings.value?.defaultVersionIndex) {
        settingsForm.value.defaultVersionIndex = settings.value?.defaultVersionIndex || null
        const startCodingVersion = jdroidStore.startCoadingVersion
        jdroidStore.setStartCoadingVersion(null)
        ideStore.initVersionIndex(startCodingVersion)
      } else
        settingsForm.value.defaultVersionIndex =
          versions.value.length > 0 ? versions.value.length - 1 : 0
    } else {
      versions.value = []
      settingsForm.value.defaultVersionIndex = null
    }
  }
)

watch(
  () => ideStore.colorMode,
  (mode) => {
    isDark.value = mode == 'dark'
  }
)

watch(
  () => ideStore.ideSettings,
  async () => {
    await initIdeSetting()
  }
)

watch(
  [() => isPremiumComputed.value, () => jdroidStore.getMoreCreditsUpdated],
  ([value1, value2]) => {
    isPremium.value = value1 || value2
  }
)
</script>

<template>
  <ideModal title="IDE Settings" class="sm:mt-[8vh]">
    <div class="flex h-full flex-col items-center gap-3">
      <p
        v-show="isCompleted"
        class="mt-1.5 text-center text-xs"
        :class="errorMsg ? 'text-red-500' : 'text-green-500'"
      >
        <FontAwesomeIcon icon="fa-circle-exclamation" class="mr-1 h-4 w-4 text-xl" />
        <span v-if="errorMsg">{{ errorMsg }}</span>
        <span v-else>Change IDE settings successfully!</span>
      </p>
      <div class="flex w-full flex-row items-center">
        <label class="text-high w-10/12 text-sm font-semibold"> Dark mode </label>
        <div
          class="text-secondary background-primary flex h-9 w-2/12 justify-center rounded-lg p-0.5 text-xs"
        >
          <button
            class="w-1/2 disabled:cursor-not-allowed"
            :class="isDark ? 'section-new-secondary-600 rounded-md text-white ' : ''"
            @click="setDarkMode(true)"
          >
            On
          </button>
          <button
            class="w-1/2 disabled:cursor-not-allowed"
            :class="!isDark ? 'section-new-secondary-600 rounded-md text-white ' : ''"
            @click="setDarkMode(false)"
          >
            Off
          </button>
        </div>
      </div>
      <div class="flex w-full flex-row items-center">
        <label class="text-high w-7/12 text-sm font-semibold"> Font size </label>
        <select
          ref="fontSizeSelect"
          id="fontSizeSelect"
          class="background-primary block w-5/12 rounded-md border-gray-200 px-4 py-2 pe-9 text-sm focus:border-blue-500 focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50 dark:border-neutral-700 dark:text-neutral-400 dark:placeholder-neutral-500 dark:focus:ring-neutral-600"
          @change="saveEvent($event)"
        >
          <option
            v-for="fontSize in Array.from({ length: 15 }, (_, i) => i + 11)"
            :key="fontSize"
            :selected="isFontsizeSelected(fontSize)"
            :value="fontSize"
          >
            {{ fontSize }}px
          </option>
        </select>
      </div>

      <div class="flex w-full flex-row items-center">
        <label class="text-high w-7/12 text-sm font-semibold">
          Editor theme
          <span
            class="background-primary ml-0.5 rounded px-2 py-1 text-xs font-medium text-purple-600"
            >Pro</span
          >
        </label>
        <select
          class="background-primary block w-5/12 rounded-md border-gray-200 px-4 py-2 pe-9 text-sm focus:border-blue-500 focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50 dark:border-neutral-700 dark:text-neutral-400 dark:placeholder-neutral-500 dark:focus:ring-neutral-600"
          v-model="settingsForm.theme"
          :disabled="!isPremium"
        >
          <option v-for="(theme, index) of themesMode" :key="index" :value="theme.value">
            {{ theme.name }}
          </option>
        </select>
      </div>
      <div class="flex w-full flex-row items-center">
        <label class="text-high w-7/12 text-sm font-semibold">
          Default language
          <span
            class="background-primary ml-0.5 rounded px-2 py-1 text-xs font-medium text-purple-600"
            >Pro</span
          >
        </label>
        <select
          id="languageSelect"
          class="background-primary block w-5/12 rounded-md border-gray-200 px-4 py-2 pe-9 text-sm focus:border-blue-500 focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50 dark:border-neutral-700 dark:text-neutral-400 dark:placeholder-neutral-500 dark:focus:ring-neutral-600"
          v-model="settingsForm.defaultLanguage"
          :disabled="!isPremium"
        >
          <option :value="null">Default IDE</option>
          <option v-for="(language, index) in languages" :key="index" :value="language.language">
            {{ language.displayName }}
          </option>
        </select>
      </div>
      <div class="flex w-full flex-row items-center">
        <label class="text-high w-7/12 text-sm font-semibold">
          Default version
          <span
            class="background-primary ml-0.5 rounded px-2 py-1 text-xs font-medium text-purple-600"
            >Pro</span
          >
        </label>
        <select
          id="versionSelect"
          class="background-primary block w-5/12 rounded-md border-gray-200 px-4 py-2 pe-9 text-sm focus:border-blue-500 focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50 dark:border-neutral-700 dark:text-neutral-400 dark:placeholder-neutral-500 dark:focus:ring-neutral-600"
          v-model="settingsForm.defaultVersionIndex"
          :disabled="!isPremium"
        >
          <option
            v-for="(version, index) in versions"
            :key="index"
            :selected="isVersionSelected(index)"
            :value="index"
          >
            {{ version }}
          </option>
        </select>
      </div>
      <div class="flex w-full flex-row items-center">
        <label class="text-high w-10/12 text-sm font-semibold">
          Default to interactive mode
          <span
            class="background-primary ml-0.5 rounded px-2 py-1 text-xs font-medium text-purple-600"
            >Pro</span
          >
        </label>
        <div
          :class="
            cn(
              'text-secondary background-primary  flex  h-9 w-2/12 justify-center rounded-lg  p-0.5 text-xs ',
              {
                ' text-neutral-400  dark:text-neutral-500': !isPremium
              }
            )
          "
        >
          <button
            class="w-1/2 disabled:cursor-not-allowed"
            :class="
              settingsForm.defaultExecutionMode
                ? 'section-new-secondary-600 rounded-md text-white  disabled:bg-new-neutral-300  disabled:text-new-neutral-400 dark:disabled:bg-neutral-700/40 dark:disabled:text-neutral-500'
                : ''
            "
            @click="settingsForm.defaultExecutionMode = true"
            :disabled="!isPremium"
          >
            On
          </button>
          <button
            class="w-1/2 disabled:cursor-not-allowed"
            :class="
              !settingsForm.defaultExecutionMode
                ? 'section-new-secondary-600 rounded-md text-white disabled:bg-new-neutral-300  disabled:text-new-neutral-400 dark:disabled:bg-neutral-700/40 dark:disabled:text-neutral-500'
                : ''
            "
            @click="settingsForm.defaultExecutionMode = false"
            :disabled="!isPremium"
          >
            Off
          </button>
        </div>
      </div>
      <div class="flex w-full flex-row items-center">
        <label for="hs-small-switch-with-icons" class="text-high w-10/12 text-sm font-semibold">
          Autocomplete code
          <span
            class="background-primary ml-0.5 rounded px-2 py-1 text-xs font-medium text-purple-600"
            >Pro</span
          >
        </label>
        <div
          :class="
            cn(
              'text-secondary background-primary  flex  h-9 w-2/12 justify-center rounded-lg  p-0.5 text-xs ',
              {
                'text-neutral-400  dark:text-neutral-500': !isPremium
              }
            )
          "
        >
          <button
            class="w-1/2 disabled:cursor-not-allowed"
            :class="
              settingsForm.autoCompleteCode
                ? 'section-new-secondary-600 rounded-md text-white  disabled:bg-new-neutral-300  disabled:text-new-neutral-400 dark:disabled:bg-neutral-700/40 dark:disabled:text-neutral-500'
                : ''
            "
            @click="settingsForm.autoCompleteCode = true"
            :disabled="!isPremium"
          >
            On
          </button>
          <button
            class="w-1/2 disabled:cursor-not-allowed"
            :class="
              !settingsForm.autoCompleteCode
                ? 'section-new-secondary-600 rounded-md text-white  disabled:bg-new-neutral-300  disabled:text-new-neutral-400 dark:disabled:bg-neutral-700/40 dark:disabled:text-neutral-500'
                : ''
            "
            @click="settingsForm.autoCompleteCode = false"
            :disabled="!isPremium"
          >
            Off
          </button>
        </div>
      </div>

      <div class="mt-2 flex w-full justify-between">
        <Button
          v-if="showGetMoreCredits"
          variant="link"
          class="pl-0"
          :data-hs-overlay="`#${IDEVIEWMODELS.JDROIDGETMORECREDITS}`"
          @click="handleUpgradePlan"
        >
          Get Pro Plan
        </Button>
        <Button
          :disabled="isLoading"
          variant="filled"
          :class="
            cn('px-8', {
              'ml-auto': !showGetMoreCredits
            })
          "
          @click="onSubmit"
        >
          Save
        </Button>
      </div>
    </div>
  </ideModal>
</template>
